<template>
  <div class="page-container">
    <h2 class="page-title">인생의 역점 무료 채널</h2>

    <div class="section">
      <h3 class="section-title">[ 안드로이드 앱(무료) ]</h3>
      <div class="section-box">
        <ul>
          <li>
            <a
              href="https://play.google.com/store/apps/details?id=kr.lydiatree.tarot"
              target="_blank"
            >
              <i class="icon">
                <img src="@/assets/sphere42.png" alt="스피어 타로 아이콘" />
              </i>
              스피어 타로(타로 vs. 화투점)
            </a>
          </li>
          <li>
            <a
              href="https://play.google.com/store/apps/details?id=kr.lydiatree.iching"
              target="_blank"
            >
              <i class="icon">
                <img src="@/assets/iching42.png" alt="주역64 아이콘" />
              </i>
              주역(周易)64
            </a>
          </li>
          <li>
            <a
              href="https://play.google.com/store/apps/details?id=kr.lydiatree.wuxing"
              target="_blank"
            >
              <i class="icon">
                <img src="@/assets/fourPillar42.png" alt="사주분석기 아이콘" />
              </i>
              사주분석기
            </a>
          </li>
        </ul>
      </div>
    </div>

    <div class="section">
      <h3 class="section-title">[ 64괘 소개 쇼츠 ]</h3>
      <div class="section-box">
        <ul>
          <li>
            <a
              href="https://youtube.com/@lydiatree-iching"
              target="_blank"
              class="youtube-link"
            >
              <i class="fab fa-youtube"></i>
              유튜브
            </a>
          </li>
          <li>
            <a
              href="https://tiktok.com/@lydiatree2025"
              target="_blank"
              class="tiktok-link"
            >
              <i class="fab fa-tiktok"></i>
              틱톡
            </a>
          </li>
        </ul>
      </div>
    </div>

    <div class="section">
      <h3 class="section-title">[ GPT ]</h3>
      <div class="section-box">
        <ul>
          <li>
            <a
              href="https://chatgpt.com/g/g-683b3346d8708191917dfd29e27a70b3-insaengyeog-yi-jeom-dijiteol-juyeog-jeomsulga"
              target="_blank"
              class="gpt-link"
            >
              <i class="fas fa-robot"></i>
              인생의 역점 GPT
            </a>
          </li>
        </ul>
      </div>
    </div>

    <div class="section">
      <h3 class="section-title">[ 네이버 블로그 ]</h3>
      <div class="section-box">
        <ul>
          <li>
            <a
              href="https://blog.naver.com/lydiatree-iching"
              target="_blank"
              class="blog-link"
            >
              <i class="fas fa-blog"></i>
              인생의 역점
            </a>
          </li>
        </ul>
      </div>
    </div>

    <div class="section">
      <h3 class="section-title">[ 괘 다운로드 ]</h3>
      <div class="section-box">
        <p class="download-description">
          후천팔괘도와 64괘, 8괘의 괘상을 소개하는 총 74장의 이미지를 담은 ZIP
          파일입니다. 괘상과 의미를 익히는데 도움이 됩니다.
          <br />
          (파일 사이즈: 5.10MB, 이미지 타입: PNG, 해상도: 1080x1920)
        </p>
        <a @click.prevent="downloadAllGuaZip" class="download-link">
          다운로드 (3,000LYD)
        </a>
      </div>
    </div>

    <div class="button-wrapper">
      <button class="primary-button" @click="goBack">돌아가기</button>
    </div>
  </div>
  <div v-if="isDownloading" class="spinner-wrapper">
    <div v-if="!downloadComplete" class="spinner"></div>
    <div v-else class="download-complete-message">{{ downloadResult }}</div>
  </div>
  <LydiaShortageModal
    v-model="showLydiaShortageModal"
    @confirm="handleModalConfirm"
    @charge="handleModalCharge"
  />
</template>

<script setup>
  import { ref } from 'vue'
  import { useRouter } from 'vue-router'
  import { useStore } from 'vuex'
  import { canUseThis, deductLydiaForMenu } from '@/utils/checkLydia'
  import LydiaShortageModal from '@/components/LydiaShortageModal.vue'

  const router = useRouter()
  const store = useStore()

  const showLydiaShortageModal = ref(false)
  // 모달 확인 버튼 클릭 핸들러
  const handleModalConfirm = () => {
    router.go(-1) // '확인' 클릭 시 IChingDaily로 이동
  }
  // 모달 충전 버튼 클릭 핸들러
  const handleModalCharge = () => {
    router.replace('/ChargeLydia') // '충전' 클릭 시 ChargeLydia로 이동 (가정)
  }

  function isMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    )
  }

  const isDownloading = ref(false)
  const downloadComplete = ref(false) // 다운로드 완료 상태
  const downloadResult = ref('') // 다운로드 결과 메시지 변수

  async function downloadAllGuaZip() {
    if (isDownloading.value) return // 이미 다운로드 중이면 중복 실행 방지

    isDownloading.value = true // 다운로드 시작 시 로딩 상태 true
    downloadComplete.value = false // 다운로드 완료 상태 초기화

    const ok = await canUseThis('downloadAll', store)
    if (!ok) {
      isDownloading.value = false // 잔액 부족 시 로딩 상태 false
      showLydiaShortageModal.value = true // 잔액 부족 시 모달 표시
    } else {
      const accessToken = localStorage.getItem('accessToken')

      try {
        const response = await fetch(
          `${import.meta.env.VITE_DOWNLOAD_ENDPOINT}/getGua/all`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ accessToken }),
          },
        )

        if (!response.ok) {
          throw new Error('서버 응답 오류')
        }

        if (isMobile()) {
          window.location.href = `${import.meta.env.VITE_DOWNLOAD_ENDPOINT}/getGua/all/${accessToken}`
          downloadResult.value = '다운로드가 시작됩니다.'
        } else {
          // PC: 기존 방식 사용
          const blob = await response.blob()
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = 'guas.zip'
          document.body.appendChild(a)
          a.click()
          a.remove()
          window.URL.revokeObjectURL(url)

          downloadResult.value = '다운로드가 완료되었습니다.'
        }

        await deductLydiaForMenu('downloadAll', store)

        downloadComplete.value = true
        setTimeout(() => {
          isDownloading.value = false
          downloadComplete.value = false
        }, 1000) // 텍스트 2초간 보여주기
      } catch (err) {
        downloadResult.value = `다운로드 중 오류가 발생했습니다.(${err.message})`
        downloadComplete.value = true
        setTimeout(() => {
          isDownloading.value = false
          downloadComplete.value = false
        }, 2000) // 텍스트 2초간 보여주기
        //await store.dispatch('refundLydia', 'downloadAll') // 다운로드 실패 시 충전 금액 환불
      }
    }
  }

  function goBack() {
    router.go(-1)
  }
</script>

<style scoped>
  .page-container {
    padding: 0.8rem 1.2rem 1.2rem;
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    width: var(--device-width);
    max-width: 480px;
    margin: auto;
    box-sizing: border-box;
  }

  .page-title {
    font-size: var(--font-size-14);
    font-weight: bold;
    line-height: 1.6;
    text-align: center;
  }

  .section {
    margin-bottom: 2rem;
  }

  .section-title {
    font-size: var(--font-size-12);
    margin-bottom: 0.2rem;
    color: #666;
    text-align: left;
    padding-left: 0.5rem;
  }

  .section-box {
    background-color: #ffffff;
    border-radius: 12px;
    padding: 1rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  li {
    margin: 0.4rem 0;
  }

  a {
    text-decoration: none;
    color: #333;
    font-size: var(--font-size-11);
    display: flex;
    align-items: center;
  }

  a i {
    margin-right: 0.5rem;
    font-size: var(--font-size-11);
  }

  .icon img {
    width: 2.0em;   /* 부모 폰트 크기와 동일하게 */
    height: 2.0em;  /* 정사각형 유지 */
    vertical-align: middle;
  }

  a.android-link {
    color: #3ddc84; /* Android green */
  }
  a.android-link i{
    font-size: 2.0rem;
  }

  a.youtube-link {
    color: #ff0000;
  }
  a.youtube-link i{
    font-size: 2.0rem;
  }

  a.tiktok-link {
    color: #000000;
  }
  a.tiktok-link i{
    font-size: 2.0rem;
  }

  a.blog-link {
    color: #03c75a;
  }
  a.blog-link i{
    font-size: 2.0rem;
  }

  a.gpt-link {
    color: #7e57c2;
  }
  a.gpt-link i{
    font-size: 2.0rem;
  }

  .download-description {
    font-size: var(--font-size-105);
    color: #444;
    margin-bottom: 0.8rem;
    line-height: 1.5;
  }

  .download-link {
    color: #1a73e8;
    font-weight: bold;
    cursor: pointer;
    font-size: var(--font-size-11);
  }
  .download-link:hover {
    text-decoration: underline;
  }

  .button-wrapper {
    margin-top: auto; /* Push button to the bottom */
    display: flex;
    justify-content: center;
    margin-bottom: 1rem;
  }

  .primary-button {
    padding: 0.7rem 1.5rem;
    font-size: var(--font-size-10);
    border: none;
    border-radius: 8px;
    background-color: #f44336; /* Red */
    color: white;
    cursor: pointer;
  }

  .primary-button:hover {
    background-color: #da190b;
  }

  .spinner-wrapper {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: var(--device-width);
    max-width: 480px;
    margin: auto;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
  .spinner {
    border: 6px solid #ccc;
    border-top: 6px solid #4caf50;
    border-radius: 50%;
    width: 48px;
    height: 48px;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .download-complete-message {
    font-size: var(--font-size-12);
    color: white;
  }
</style>
